import { GainerData } from './gainersService';
import type { SubnetMindshare } from '../types/mindshare';

interface CacheItem<T> {
  data: T;
  timestamp: number;
}

interface CacheData {
  holderGainers: CacheItem<GainerData[]>;
  twitterGainers: CacheItem<GainerData[]>;
  volumeGainers: CacheItem<GainerData[]>;
  mindshareData: CacheItem<SubnetMindshare[]>;
  percentChanges: CacheItem<Record<number, number>>;
  subnetHistories: CacheItem<Record<number, any>>;
}

class CacheService {
  private static instance: CacheService;
  private cache: Partial<CacheData> = {};
  private refreshCallbacks: (() => Promise<void>)[] = [];
  private isProcessingQueue = false;
  private readonly CACHE_EXPIRATION = 15 * 60 * 1000; // 15 minutes
  private readonly MAX_HISTORY_ITEMS = 100; // Limit history items per subnet
  private readonly MAX_CACHE_SIZE = 4.5 * 1024 * 1024; // 4.5MB (safe limit for most browsers)

  private constructor() {
    this.initializeCache();
    
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.persistCache();
      });
      
      this.loadCacheFromStorage();
    }
  }

  public static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }

  private initializeCache() {
    this.cache = {};
  }

  private loadCacheFromStorage() {
    try {
      const storedCache = localStorage.getItem('mindshare_cache');
      if (storedCache) {
        const decompressedCache = this.decompressData(storedCache);
        this.cache = JSON.parse(decompressedCache);
      }
    } catch (error) {
      console.error('Error loading cache from localStorage:', error);
      this.clearCache(); // Reset cache if corrupted
    }
  }

  private compressData(data: string): string {
    // Simple compression by removing whitespace
    return data.replace(/\s+/g, '');
  }

  private decompressData(data: string): string {
    // No decompression needed for whitespace removal
    return data;
  }

  private trimHistoricalData<T>(data: T[]): T[] {
    if (Array.isArray(data) && data.length > this.MAX_HISTORY_ITEMS) {
      return data.slice(-this.MAX_HISTORY_ITEMS);
    }
    return data;
  }

  private getCacheSize(): number {
    const cacheStr = JSON.stringify(this.cache);
    return new Blob([cacheStr]).size;
  }

  private evictOldestEntries() {
    const entries = Object.entries(this.cache);
    entries.sort((a, b) => {
      const aTimestamp = (a[1] as CacheItem<any>).timestamp;
      const bTimestamp = (b[1] as CacheItem<any>).timestamp;
      return aTimestamp - bTimestamp;
    });

    while (this.getCacheSize() > this.MAX_CACHE_SIZE && entries.length > 0) {
      const [key] = entries.shift()!;
      delete this.cache[key as keyof CacheData];
    }
  }

  private persistCache() {
    try {
      // Trim historical data before persisting
      if (this.cache.subnetHistories) {
        const histories = this.cache.subnetHistories.data;
        Object.keys(histories).forEach(key => {
          histories[key] = this.trimHistoricalData(histories[key]);
        });
      }

      if (this.cache.mindshareData) {
        this.cache.mindshareData.data = this.trimHistoricalData(this.cache.mindshareData.data);
      }

      // Check cache size and evict if necessary
      if (this.getCacheSize() > this.MAX_CACHE_SIZE) {
        this.evictOldestEntries();
      }

      const cacheString = JSON.stringify(this.cache);
      const compressedCache = this.compressData(cacheString);
      
      try {
        localStorage.setItem('mindshare_cache', compressedCache);
      } catch (e) {
        if (e.name === 'QuotaExceededError' || e.toString().includes('exceeded the quota')) {
          this.evictOldestEntries();
          // Try one more time after eviction
          localStorage.setItem('mindshare_cache', this.compressData(JSON.stringify(this.cache)));
        } else {
          throw e;
        }
      }
    } catch (error) {
      console.error('Error persisting cache to localStorage:', error);
      // Emit an event that UI can listen to for showing user feedback
      window.dispatchEvent(new CustomEvent('cacheError', {
        detail: {
          message: 'Unable to save cache. Try clearing your browser data if this persists.'
        }
      }));
    }
  }

  public setCache<K extends keyof CacheData>(key: K, data: CacheData[K]['data']) {
    // Trim historical data before caching
    let processedData = data;
    if (Array.isArray(data)) {
      processedData = this.trimHistoricalData(data) as typeof data;
    }

    this.cache[key] = {
      data: processedData,
      timestamp: Date.now()
    } as CacheData[K];
    
    this.persistCache();
  }

  public getCache<K extends keyof CacheData>(key: K): CacheData[K]['data'] | null {
    const cacheItem = this.cache[key] as CacheItem<CacheData[K]['data']> | undefined;
    
    if (!cacheItem) {
      return null;
    }
    
    if (Date.now() - cacheItem.timestamp > this.CACHE_EXPIRATION) {
      delete this.cache[key];
      return null;
    }
    
    return cacheItem.data;
  }

  public clearCache() {
    this.cache = {};
    try {
      localStorage.removeItem('mindshare_cache');
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  public registerRefreshCallback(callback: () => Promise<void>) {
    this.refreshCallbacks.push(callback);
  }

  public async refreshAll() {
    if (this.isProcessingQueue) return;
    
    this.isProcessingQueue = true;
    
    try {
      await Promise.all(this.refreshCallbacks.map(callback => callback()));
      this.persistCache();
    } catch (error) {
      console.error('Error refreshing cache:', error);
    } finally {
      this.isProcessingQueue = false;
    }
  }

  public destroy() {
    this.clearCache();
    this.refreshCallbacks = [];
    this.isProcessingQueue = false;
    
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', () => {
        this.persistCache();
      });
    }
  }

  public getCacheStats() {
    return {
      totalItems: Object.keys(this.cache).length,
      keys: Object.keys(this.cache),
      cacheSize: `${(this.getCacheSize() / 1024 / 1024).toFixed(2)}MB`,
      expirationTime: this.CACHE_EXPIRATION / 1000 / 60 + ' minutes'
    };
  }
  
  public isDataCached(requiredKeys: (keyof CacheData)[]): boolean {
    return requiredKeys.every(key => this.getCache(key) !== null);
  }
}

export const cacheService = CacheService.getInstance();